import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'basic-components';
import { DataStorageEnums } from 'dashboard-services/api/dataStorage';

import { settingsCurvesActions } from 'actions/curves';
import getApiConfig from 'api/ApiConfig';
import { settingsSeriesQueries } from 'queries/series';
import { CurvesUtils, FormulaUtils } from 'utils';

import DataRange from 'primary/DataRange';
import { DateFormat, OutputType, SetAsDefaultSettings, TimeAndDate, TimeZone } from 'primary/TsSettings';

import ContractSort from './ContractSort';
import FillBlanks from './FillBlanks';
import IsRelative from './IsRelative';
import LimitContracts from './LimitContracts';
import PeriodType from './PeriodType';

import './CurveSettings.scss';

const CurveSettings = () => {
  const dispatch = useDispatch(),
        [keyQuery, setKeyQuery] = useState(),
        { 
          range,
          lastTypeAmount,
          lastType,
          from,
          to,
          fromSelected,
          toSelected,
          csvHeaders,
          timeZone,
          dateFormat,
          periodType,
          symbolSize,
          contractSort,
          isRelative,
          fillBlanks,
          formulaExpressions = {}
        } = useSelector(state => state.curvesState.settingsState),
        source = useSelector(state => state.globalState.localSource),
        selectedCurves = useSelector(state => state.curvesState.creatorState.selectedCurves),
        onSaveDefaultSettings = useCallback(() => dispatch(settingsCurvesActions.saveDefaultSettings()), [dispatch]),
        onClearDefaultSettings = useCallback(() => dispatch(settingsCurvesActions.clearDefaultSettings()), [dispatch]),
        onChange = useCallback((val, { name }) => dispatch(settingsCurvesActions.onChange(val, name)), [dispatch]),
        targetSource = useMemo(() => {
          if (selectedCurves?.[0]?.source) {
            return selectedCurves[0].source
          } else {
            return source
          }
        }, [selectedCurves, source]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        { isFetching: isFetchingKeys, isPending: isPendingKeys, data: keys } = settingsSeriesQueries.useKeysForGroups({ 
          input: keyQuery,
          selectedSymbols: selectedCurves,
          getApiConfig: getApiConfigLocal,
          getMetadataFilter: settingsCurvesActions.getMetadataFilter,
          getSelect: settingsCurvesActions.getSelect,
          provider: targetSource
        })

  return (
    <div className="ng-office-app__authed__content__basket__body__settings">
      <SetAsDefaultSettings onSave={onSaveDefaultSettings}/>
      <OutputType
          additionalCsvHeaders={CurvesUtils.CSV_HEADER_OPTIONS}
          csvHeaders={csvHeaders}
          csvHeadersOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.csvHeaders)}
          formatType={DataStorageEnums.DATA_FORMAT.CSV}
          isPendingKeys={isPendingKeys || isFetchingKeys}
          keys={keys}
          onChange={onChange}
          setKeyQuery={setKeyQuery}
          withOutputType={false}
          withTranspose={false}
      >
        <LimitContracts
            onChange={onChange}
            symbolSizeOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.symbolSize)}
            value={symbolSize}
        />
        <FillBlanks
            fillBlanksOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.fillBlanks)}
            onChange={onChange}
            value={fillBlanks}
        />
      </OutputType>
      <TimeAndDate>
        <PeriodType
            onChange={onChange}
            periodTypeOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.periodType)}
            value={periodType}
        />
        <DataRange
            from={from}
            fromOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.startDate)}
            fromSelected={fromSelected}
            lastType={lastType}
            lastTypeAmount={lastTypeAmount}
            lastTypeAmountOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.lastTypeAmount)}
            lastTypeOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.lastType)}
            onChange={onChange}
            range={range}
            rangeOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.range)}
            to={to}
            toOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.endDate)}
            toSelected={toSelected}
            withAdditionalToday
        />
        <TimeZone
            onChange={onChange}
            timeZone={timeZone}
            timeZoneOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.timeZone)}
        />
        <DateFormat
            dateFormat={dateFormat}
            dateFormatOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.dateFormat)}
            onChange={onChange}
        />
        <ContractSort
            contractSortOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.contractSort)}
            onChange={onChange}
            value={contractSort}
        />
        <IsRelative
            isRelativeOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.isRelative)}
            onChange={onChange}
            value={isRelative}
        />
      </TimeAndDate>
      <div className="ng-office-app__authed__content__basket__body__settings__reset">
        <Button
            onClick={onClearDefaultSettings}
            size="small"
            variant="text"
        >
          Reset default settings
        </Button>
      </div>
    </div>
  )
}

export default CurveSettings;
